import type { RunnerAttention } from '../domains/electron';

// What the runner badge lists (web RunnerMenu and the board header button,
// mobile runner pill). Web and mobile paint the same list, so the order, the
// levels and the copy live here. Red ("broken") is for what stops work now:
// the runner offline, setup failed, or jobs that cannot run. Everything else
// is amber ("needs-you").

/** Where an item's action goes: a Runner Settings tab, or the self-update. */
export type RunnerAttentionTarget = 'setup' | 'secrets' | 'jobs' | 'update';

export type RunnerAttentionItem = {
  id:
    | 'offline'
    | 'setup-failed'
    | 'missing-secrets'
    | 'repo'
    | 'no-jobs'
    | 'update';
  level: 'needs-you' | 'broken';
  text: string;
  /** Absent when there is nothing to open (an offline runner has no
   *  settings to show until it reconnects). */
  action?: { label: string; target: RunnerAttentionTarget };
};

export type RunnerAttentionInput = {
  /** The picked runner is registered but not connected. */
  offline: boolean;
  /** The board's setup ended in an error (the header's old red state). */
  setupFailed: boolean;
  /** Owner only: a newer runner version is published. */
  updateAvailable: boolean;
  /** The runner's `attention` answer. Null for a member, and until it
   *  answers. */
  attention: RunnerAttention | null;
};

/** Names shown before the list is cut to "…". */
const NAMES_SHOWN = 3;

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

function missingText(missing: string[], blocked: string[]): string {
  const names = missing.slice(0, NAMES_SHOWN).join(', ');
  const more = missing.length > NAMES_SHOWN ? ', …' : '';
  const base = `${plural(missing.length, 'secret')} missing: ${names}${more}`;
  if (blocked.length === 0) return base;
  return `${base} (${plural(blocked.length, 'job')} can't run)`;
}

/** The list, worst first. An offline runner answers nothing else. */
export function runnerAttentionItems(
  input: RunnerAttentionInput
): RunnerAttentionItem[] {
  if (input.offline) {
    return [
      {
        id: 'offline',
        level: 'broken',
        text: 'Runner offline. Start it on its machine.'
      }
    ];
  }
  const items: RunnerAttentionItem[] = [];
  if (input.setupFailed) {
    items.push({
      id: 'setup-failed',
      level: 'broken',
      text: 'Setup failed',
      action: { label: 'Open Setup', target: 'setup' }
    });
  }
  const a = input.attention;
  if (a?.missingSecrets && a.missingSecrets.length > 0) {
    items.push({
      id: 'missing-secrets',
      level: a.blockedJobs.length > 0 ? 'broken' : 'needs-you',
      text: missingText(a.missingSecrets, a.blockedJobs),
      // Adding a value takes the store password; the Secrets tab asks for it
      // first when the store is locked.
      action: {
        label: a.secretsLocked ? 'Unlock and add' : 'Add',
        target: 'secrets'
      }
    });
  }
  if (a && !a.repoConnected) {
    items.push({
      id: 'repo',
      level: 'needs-you',
      text: 'Repository not connected',
      action: { label: 'Connect', target: 'setup' }
    });
  }
  // Only once the clone has been read: before it, "no jobs" is a guess.
  if (a?.repoConnected && a.missingSecrets !== null && a.jobsCount === 0) {
    items.push({
      id: 'no-jobs',
      level: 'needs-you',
      text: 'No jobs for this board',
      action: { label: 'How to add jobs', target: 'jobs' }
    });
  }
  if (input.updateAvailable) {
    items.push({
      id: 'update',
      level: 'needs-you',
      text: 'Update available',
      action: { label: 'Update', target: 'update' }
    });
  }
  // Stable sort: broken first, each level in the order pushed.
  return [
    ...items.filter((i) => i.level === 'broken'),
    ...items.filter((i) => i.level === 'needs-you')
  ];
}

/** The badge's color: the worst level in the list, or null for no badge. */
export function runnerAttentionLevel(
  items: RunnerAttentionItem[]
): RunnerAttentionItem['level'] | null {
  if (items.some((i) => i.level === 'broken')) return 'broken';
  return items.length > 0 ? 'needs-you' : null;
}
